import React from "react";
import { Link, Navigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2, GitCompareArrows, Trophy } from "lucide-react";
import { toast } from "sonner";

import AdminHeader from "@/components/admin/AdminHeader";
import { useAuth } from "@/contexts/AuthContext";
import { getConfiguredSeason } from "@/lib/season";
import queryClient from "@/lib/queryClient";
import {
  deleteAdminPlayoffSeriesResolution,
  getAdminPlayoffSeriesResolutions,
  upsertAdminPlayoffSeriesResolution,
} from "@/api/adminMatches";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatSvDateTime } from "@/utils/date";

const RESOLUTION_LABELS = {
  runoff: "Skiljeheat",
  official_decision: "Officiellt beslut",
  aggregate: "Sammanlagd poäng",
};

function seriesKey(item) {
  return `${item?.stage_type || "stage"}:${item?.stage_group || "group"}`;
}

function SeriesSummary({ item }) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">{item.stage_label}</Badge>
        <strong className="text-sm text-foreground">{item.group_label}</strong>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {(item.teams || []).map((team) => (
          <div key={team.id} className="flex items-center justify-between rounded-xl border border-border/70 bg-card px-3 py-2.5">
            <span className="text-sm font-medium text-foreground">{team.name}</span>
            <b className="text-lg text-foreground">{team.score}</b>
          </div>
        ))}
      </div>
      <div className="space-y-1.5">
        {(item.matches || []).map((match) => (
          <div key={match.id} className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>{match.home_team}–{match.away_team}</span>
            <span>{match.home_score}–{match.away_score} · {formatSvDateTime(match.date, { dateStyle: "short" })}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminPlayoffSeriesResolutionsPage() {
  const { user } = useAuth();
  const [season, setSeason] = React.useState(getConfiguredSeason());
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [resolutionTypes, setResolutionTypes] = React.useState({});
  const [pendingDecision, setPendingDecision] = React.useState(null);

  const seasonOptions = React.useMemo(() => {
    const base = Number(getConfiguredSeason());
    return [base + 1, base, base - 1, base - 2, base - 3].map(String);
  }, []);
  const unresolved = items.filter((item) => item.requires_resolution);
  const manuallyResolved = items.filter((item) => item.is_tie && item.complete && item.resolution_source === "manual");

  const refresh = React.useCallback(async () => {
    const response = await getAdminPlayoffSeriesResolutions(season);
    setItems(Array.isArray(response?.items) ? response.items : []);
  }, [season]);

  React.useEffect(() => {
    if (user?.role !== "super_admin") return undefined;
    let active = true;
    setLoading(true);
    getAdminPlayoffSeriesResolutions(season)
      .then((response) => {
        if (active) setItems(Array.isArray(response?.items) ? response.items : []);
      })
      .catch((error) => toast.error(error?.message || "Kunde inte hämta slutspelsavgöranden"))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [season, user?.role]);

  if (user?.role !== "super_admin") return <Navigate to="/admin" replace />;

  const saveDecision = async () => {
    if (!pendingDecision || saving) return;
    const { item, team } = pendingDecision;
    setSaving(true);
    try {
      const resolutionType = resolutionTypes[seriesKey(item)] || "runoff";
      await upsertAdminPlayoffSeriesResolution({
        season: Number(season),
        stage_type: item.stage_type,
        stage_group: item.stage_group,
        winner_team_id: team.id,
        resolution_type: resolutionType,
        note: resolutionType === "runoff" ? "Vinnare efter skiljeheat." : "Vinnare enligt officiellt beslut.",
      });
      await refresh();
      await queryClient.invalidateQueries({ queryKey: ["playoffs", Number(season)] });
      setPendingDecision(null);
      toast.success(`${team.name} är registrerat som vinnare.`);
    } catch (error) {
      toast.error(error?.message || "Kunde inte spara slutspelsvinnaren");
    } finally {
      setSaving(false);
    }
  };

  const clearDecision = async (item) => {
    if (saving) return;
    setSaving(true);
    try {
      await deleteAdminPlayoffSeriesResolution(season, item.stage_type, item.stage_group);
      await refresh();
      await queryClient.invalidateQueries({ queryKey: ["playoffs", Number(season)] });
      toast.success("Det manuella avgörandet togs bort.");
    } catch (error) {
      toast.error(error?.message || "Kunde inte ta bort avgörandet");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen">
      <AdminHeader />
      <main className="container mx-auto px-4 py-6">
        <div className="space-y-6">
          <Card className="overflow-hidden border-border/70 shadow-sm">
            <CardHeader className="relative gap-4 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-white">
              <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15"><Trophy className="h-5 w-5" /></div>
                  <div>
                    <CardTitle className="text-xl text-white sm:text-2xl">Slutspelsavgöranden</CardTitle>
                    <CardDescription className="mt-1 text-slate-200">Tilldela officiell vinnare när en slutspelsserie slutar oavgjord sammanlagt.</CardDescription>
                  </div>
                </div>
                <Select value={String(season)} onValueChange={(value) => setSeason(Number(value))}>
                  <SelectTrigger className="w-full border-white/10 bg-white/10 text-white sm:w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>{seasonOptions.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </CardHeader>
          </Card>

          <Card className={unresolved.length ? "border-amber-500/40 shadow-sm" : "border-border/70 shadow-sm"}>
            <CardHeader>
              <div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /><CardTitle>Kräver beslut ({unresolved.length})</CardTitle></div>
              <CardDescription>Båda mötena är färdigspelade men totalpoängen är lika. Välj vinnaren först när skiljeheatet eller det officiella beslutet är klart.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading ? <p className="text-sm text-muted-foreground">Hämtar slutspelsserier...</p> : unresolved.length ? unresolved.map((item) => (
                <div key={seriesKey(item)} className="space-y-4 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4">
                  <SeriesSummary item={item} />
                  <Select value={resolutionTypes[seriesKey(item)] || "runoff"} onValueChange={(value) => setResolutionTypes((current) => ({ ...current, [seriesKey(item)]: value }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="runoff">Skiljeheat</SelectItem><SelectItem value="official_decision">Officiellt beslut</SelectItem></SelectContent>
                  </Select>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {(item.teams || []).map((team) => <Button key={team.id} type="button" disabled={saving} onClick={() => setPendingDecision({ item, team })}>Välj {team.name} som vinnare</Button>)}
                  </div>
                </div>
              )) : <div className="rounded-xl border border-dashed p-5 text-sm text-muted-foreground">Inga oavgjorda slutspelsserier kräver beslut just nu.</div>}
            </CardContent>
          </Card>

          <Card className="border-border/70 shadow-sm">
            <CardHeader><div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /><CardTitle>Manuellt avgjorda</CardTitle></div><CardDescription>Historik över oavgjorda serier där en officiell vinnare har registrerats.</CardDescription></CardHeader>
            <CardContent className="space-y-3">
              {manuallyResolved.length ? manuallyResolved.map((item) => (
                <div key={seriesKey(item)} className="space-y-3 rounded-2xl border border-border/70 p-4">
                  <SeriesSummary item={item} />
                  <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
                    <div className="flex items-center gap-2 text-sm"><Trophy className="h-4 w-4 text-amber-500" /><strong>{item.winner_team}</strong><Badge variant="secondary">{RESOLUTION_LABELS[item.resolution_type] || "Manuellt beslut"}</Badge></div>
                    <Button type="button" variant="outline" disabled={saving} onClick={() => clearDecision(item)}>Rensa avgörande</Button>
                  </div>
                </div>
              )) : <p className="text-sm text-muted-foreground">Ingen historik för vald säsong.</p>}
            </CardContent>
          </Card>

          <Button asChild variant="outline"><Link to="/admin"><GitCompareArrows className="mr-2 h-4 w-4" />Till adminöversikten</Link></Button>
        </div>
      </main>

      <AlertDialog open={Boolean(pendingDecision)} onOpenChange={(open) => { if (!open && !saving) setPendingDecision(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Bekräfta slutspelsvinnare</AlertDialogTitle><AlertDialogDescription>Du registrerar {pendingDecision?.team?.name} som vinnare i {pendingDecision?.item?.group_label}. Slutspelsvyn kommer därefter att visa laget som vinnare.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel disabled={saving}>Avbryt</AlertDialogCancel><AlertDialogAction disabled={saving} onClick={saveDecision}>{saving ? "Sparar..." : "Bekräfta vinnare"}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
